/**
 * How a discrepancy row reads on every surface (dialog chip, section header,
 * summary matrix, filters, export). Stored status is unchanged: an open missing
 * e-Manifest row stays UNRESOLVED so the exception report job and customer
 * notifications keep firing, but it reads as PENDING because the next step is
 * a correction in e-Manifest. Once follow-up starts (stored PENDING) it reads
 * as FOLLOW_UP, its own group everywhere it is counted or filtered.
 *
 * Accepts the upper-case Prisma / GraphQL values and is case-insensitive.
 * No Prisma or Node imports: Oak imports this file through Vite.
 */
export type DiscrepancyDisplayBucket =
  | "UNRESOLVED"
  | "PENDING"
  | "FOLLOW_UP"
  | "RESOLVED"
  | "IGNORED";

export const FOLLOW_UP_LABEL = "Follow-up in progress";

/**
 * Staff-only value of the Discrepancy Status filter that lists missing
 * e-Manifest rows in follow-up. A filter value only: it is not a stored status.
 */
export const FOLLOW_UP_FILTER_VALUE = "FOLLOW_UP";

export type DiscrepancyDisplayRow = {
  status?: string | null;
  kind?: string | null;
};

const EMANIFEST_MISSING_KIND = "EMANIFEST_MISSING";

export function isEManifestMissingKind(kind: string | null | undefined): boolean {
  return kind?.toUpperCase() === EMANIFEST_MISSING_KIND;
}

function storedBucket(status: string | null | undefined): DiscrepancyDisplayBucket {
  switch (status?.toUpperCase()) {
    case "PENDING":
      return "PENDING";
    case "RESOLVED":
      return "RESOLVED";
    case "IGNORED":
      return "IGNORED";
    default:
      return "UNRESOLVED";
  }
}

/** The status column a row is counted under and filtered by. */
export function getDiscrepancyDisplayBucket(row: DiscrepancyDisplayRow): DiscrepancyDisplayBucket {
  const bucket = storedBucket(row.status);
  if (!isEManifestMissingKind(row.kind)) return bucket;
  if (bucket === "UNRESOLVED") return "PENDING";
  if (bucket === "PENDING") return "FOLLOW_UP";
  return bucket;
}

/**
 * The label a missing e-Manifest row overrides; null for every other row, which
 * keeps the label its surface already prints.
 */
export function getDiscrepancyDisplayLabel(row: DiscrepancyDisplayRow): string | null {
  if (!isEManifestMissingKind(row.kind)) return null;
  switch (storedBucket(row.status)) {
    case "UNRESOLVED":
      return "Needs correction in e-Manifest";
    case "PENDING":
      return FOLLOW_UP_LABEL;
    default:
      return null;
  }
}
